import { countries, defaultLocale } from './available-locales';
import { Locale } from './locale.model';

export function getLocale(localeString?: string) {
  if (localeString) {
    return convertLocaleStringToLocaleObject(localeString);
  }
  try {
    return getLocaleFromUrl();
  } catch (error) {
    return typeof window !== 'undefined' ? convertLocaleStringToLocaleObject(window.navigator.language) : defaultLocale;
  }
}

export function getRedirectFromState():
  | {
      locale: Locale;
      path: string;
      search: string;
    }
  | undefined {
  if (typeof window !== 'undefined') {
    const matches = window.location.search.match(new RegExp(`state=([^&]*)`));
    if (matches) {
      const pathFromState = decodeURIComponent(matches[1]).split('|')[0].replace(/^\/+/, ''); // .replace() removes leading slashes

      if (pathFromState) {
        const [locale, ...rest] = pathFromState.split('/');
        const path = rest ? rest.join('/') : undefined;

        return locale && path
          ? {
              locale: convertLocaleStringToLocaleObject(locale),
              path,
              search: window.location.search,
            }
          : undefined;
      }
    }
  }

  return undefined;
}

export function getLocaleFromUrl(path?: string): Locale {
  const match = (path || window.location.pathname).match(/^\/([a-zA-Z]{2}-[a-zA-Z]{2})(\/{0,1}$|\/.*)/);
  const locale = match && match[1];
  if (!locale) {
    throw new Error("Can't find locale in url");
  }

  return convertLocaleStringToLocaleObject(locale);
}

export function getLocaleFromUrlOrState(): Locale {
  try {
    return getLocaleFromUrl();
  } catch {
    try {
      return getRedirectFromState()?.locale || defaultLocale;
    } catch {
      return defaultLocale;
    }
  }
}

export function getDefaultLocaleFromCountry(country: string) {
  return countries[country.toLocaleLowerCase()].find((locale) => locale.defaultLanguage);
}

export function getLocaleForCountry(country: string) {
  const locale = getLocale();
  if (locale && isValidLocale(locale, country)) {
    return locale;
  }

  return getDefaultLocaleFromCountry(country);
}

function isValidLocale(locale: Locale, country: string) {
  return !!countries[country.toLocaleLowerCase()].find((_locale) => locale.RFC5646 === _locale.RFC5646);
}

export function convertLocaleStringToLocaleObject(locale: string): Locale {
  const [language, country] = locale
    .toLocaleLowerCase()
    .split('-')
    .map((localePart) => localePart.toLocaleLowerCase());
  return new Locale(language, country);
}
